/**
 * OneTrust consent management utilities
 */

import { getCookie } from './cookie-utils'
import type { OneTrustConsentPreferences } from './types'
import { OneTrustCategories } from './types'

/**
 * Default OneTrust consent string
 */
export const OPTANON_DEFAULT_CONSENT = 'C0001:1,C0003:0,C0004:0,C0002:0'

/**
 * OneTrust consent cookie name
 */
export const OPTANON_COOKIE_NAME = 'OptanonConsent'

/**
 * Consent to assume when the visitor has no OptanonConsent cookie, as a
 * consent string in the same shape as the cookie's `groups` value.
 */
let assumedConsent: string | undefined

/**
 * Serialises consent preferences into OneTrust's `C0001:1,C0002:0,...` shape,
 * so an assumed set flows through the same parsing as a real cookie.
 */
const serialiseConsentPreferences = ({
  categoryPreferences,
}: OneTrustConsentPreferences): string =>
  Object.entries(categoryPreferences)
    .map(([category, isGranted]) => `${category}:${isGranted ? '1' : '0'}`)
    .join(',')

/**
 * Declares the consent to assume for an application that runs without a
 * OneTrust banner — an in-store colleague device, for example. Every consent
 * reader in this package then resolves against it instead of
 * {@link OPTANON_DEFAULT_CONSENT}.
 *
 * A real OptanonConsent cookie always wins: this never overrides a choice an
 * actual visitor made. Pass `undefined` to clear.
 *
 * @param preferences - The consent to assume, or undefined to clear it
 */
export const setAssumedConsent = (
  preferences: OneTrustConsentPreferences | undefined,
): void => {
  assumedConsent = preferences
    ? serialiseConsentPreferences(preferences)
    : undefined
}

/**
 * Gets the OneTrust consent string from cookies
 * @returns The consent string; when no cookie is present, the assumed consent
 * set via {@link setAssumedConsent}, otherwise {@link OPTANON_DEFAULT_CONSENT}
 */
export const getOneTrustConsent = (): string => {
  let consentOnetrust = assumedConsent ?? OPTANON_DEFAULT_CONSENT
  let cookieOptanonConsent = getCookie(OPTANON_COOKIE_NAME)

  if (cookieOptanonConsent) {
    cookieOptanonConsent = decodeURIComponent(cookieOptanonConsent)

    if (cookieOptanonConsent.indexOf('&groups=') !== -1) {
      consentOnetrust = cookieOptanonConsent.split('&groups=')[1].split('&')[0]
    }
  }

  return consentOnetrust
}

/**
 * Transforms a OneTrust consent string into a structured object
 * @param consent - The consent string to transform
 * @returns Object containing category preferences
 */
export const transformOneTrustConsent = (
  consent: string,
): OneTrustConsentPreferences => {
  const categories = consent
    .split(',')
    .reduce((acc: Record<string, boolean>, category: string) => {
      const [key, value] = category.split(':')
      acc[key] = value === '1'
      return acc
    }, {})

  return {
    categoryPreferences: categories,
  }
}

/**
 * Gets and transforms OneTrust consent in one call
 * @returns Object containing category preferences from current consent
 */
export const getOneTrustConsentPreferences = (): Record<string, boolean> => {
  const consent = getOneTrustConsent()
  const { categoryPreferences } = transformOneTrustConsent(consent)
  return categoryPreferences
}

/**
 * Checks if a specific consent category is enabled
 * @param category - The category to check (e.g., 'C0001', 'C0002')
 * @returns True if the category is enabled, false otherwise
 */
export const isConsentCategoryEnabled = (category: string): boolean => {
  const preferences = getOneTrustConsentPreferences()
  return preferences[category] === true
}

// Re-export the categories enum for convenience
export { OneTrustCategories }
